import type { JSONSchema7 } from "json-schema"
import { JSON_STRING_FORMATS } from "./model"

/**
 * JSON Schema that validates JsonField structure
 * This is used by CodeMirror to provide real-time validation and autocomplete
 */
export const jsonFieldMetaSchema: JSONSchema7 = {
  $schema: "http://json-schema.org/draft-07/schema#",
  $ref: "#/definitions/JsonField",
  definitions: {
    BaseType: {
      type: "string",
      enum: ["string", "number", "integer", "boolean", "object", "array"],
    },
    NullableTypeArray: {
      type: "array",
      minItems: 2,
      maxItems: 2,
      items: {
        type: "string",
        enum: [
          "string",
          "number",
          "integer",
          "boolean",
          "object",
          "array",
          "null",
        ],
      },
      contains: { const: "null" },
      allOf: [{ contains: { $ref: "#/definitions/BaseType" } }],
    },
    StringType: {
      anyOf: [
        { const: "string" },
        {
          allOf: [
            { $ref: "#/definitions/NullableTypeArray" },
            { contains: { const: "string" } },
          ],
        },
      ],
    },
    NumberType: {
      anyOf: [
        { const: "number" },
        {
          allOf: [
            { $ref: "#/definitions/NullableTypeArray" },
            { contains: { const: "number" } },
          ],
        },
      ],
    },
    IntegerType: {
      anyOf: [
        { const: "integer" },
        {
          allOf: [
            { $ref: "#/definitions/NullableTypeArray" },
            { contains: { const: "integer" } },
          ],
        },
      ],
    },
    BooleanType: {
      anyOf: [
        { const: "boolean" },
        {
          allOf: [
            { $ref: "#/definitions/NullableTypeArray" },
            { contains: { const: "boolean" } },
          ],
        },
      ],
    },
    ObjectType: {
      anyOf: [
        { const: "object" },
        {
          allOf: [
            { $ref: "#/definitions/NullableTypeArray" },
            { contains: { const: "object" } },
          ],
        },
      ],
    },
    ArrayType: {
      anyOf: [
        { const: "array" },
        {
          allOf: [
            { $ref: "#/definitions/NullableTypeArray" },
            { contains: { const: "array" } },
          ],
        },
      ],
    },

    JsonField: {
      title: "JSON Field Schema",
      description:
        "Schema for a typed JSON field. The 'type' property determines which additional properties are available.",
      type: "object",
      required: ["type"],
      allOf: [
        {
          properties: {
            type: {
              description:
                "The field type: string, number, integer, boolean, object, or array. Nullable form: [T, 'null'] or ['null', T].",
              anyOf: [
                { $ref: "#/definitions/BaseType" },
                { $ref: "#/definitions/NullableTypeArray" },
              ],
            },
            description: {
              type: "string",
              description: "Optional description of the field",
            },
          },
        },
        {
          if: {
            properties: {
              type: { $ref: "#/definitions/StringType" },
            },
            required: ["type"],
          },
          then: {
            properties: {
              minLength: {
                type: "integer",
                minimum: 0,
                description: "Minimum string length",
              },
              maxLength: {
                type: "integer",
                minimum: 0,
                description: "Maximum string length",
              },
              format: {
                type: "string",
                enum: [...JSON_STRING_FORMATS],
                description: "Semantic format hint for the string value",
              },
              enum: {
                type: "array",
                items: { type: "string" },
                minItems: 1,
                uniqueItems: true,
                description: "Allowed values for the field",
              },
              description: {
                type: "string",
                description: "Optional description of the field",
              },
            },
          },
        },
        {
          if: {
            properties: {
              type: {
                anyOf: [
                  { $ref: "#/definitions/NumberType" },
                  { $ref: "#/definitions/IntegerType" },
                ],
              },
            },
            required: ["type"],
          },
          then: {
            properties: {
              minimum: {
                type: "number",
                description: "Minimum value",
              },
              maximum: {
                type: "number",
                description: "Maximum value",
              },
              enum: {
                type: "array",
                items: { type: "number" },
                minItems: 1,
                uniqueItems: true,
                description: "Allowed values for the field",
              },
              description: {
                type: "string",
                description: "Optional description of the field",
              },
            },
          },
        },
        {
          // Narrows the number-wide enum above: allOf intersects, so integer wins.
          if: {
            properties: {
              type: { $ref: "#/definitions/IntegerType" },
            },
            required: ["type"],
          },
          then: {
            properties: {
              enum: {
                items: { type: "integer" },
              },
            },
          },
        },
        {
          if: {
            properties: {
              type: { $ref: "#/definitions/BooleanType" },
            },
            required: ["type"],
          },
          then: {
            properties: {
              description: {
                type: "string",
                description: "Optional description of the field",
              },
            },
            additionalProperties: false,
          },
        },
        {
          if: {
            properties: {
              type: { $ref: "#/definitions/ObjectType" },
            },
            required: ["type"],
          },
          then: {
            required: ["properties", "required", "additionalProperties"],
            properties: {
              properties: {
                type: "object",
                description: "Object properties mapping keys to JsonFields.",
                additionalProperties: {
                  $ref: "#/definitions/JsonField",
                },
              },
              required: {
                type: "array",
                description: "Required property names",
                items: { type: "string" },
              },
              additionalProperties: {
                const: false,
                description: "Must be false",
              },
              description: {
                type: "string",
                description: "Optional description of the field",
              },
            },
          },
        },
        {
          if: {
            properties: {
              type: { $ref: "#/definitions/ArrayType" },
            },
            required: ["type"],
          },
          then: {
            required: ["items"],
            properties: {
              items: {
                $ref: "#/definitions/JsonField",
                description: "Schema for array items",
              },
              minItems: {
                type: "integer",
                minimum: 0,
                description: "Minimum array length",
              },
              maxItems: {
                type: "integer",
                minimum: 0,
                description: "Maximum array length",
              },
              description: {
                type: "string",
                description: "Optional description of the field",
              },
            },
          },
        },
      ],
    },
  },
}
